import { useState, useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from './ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog'
import { mdiDragVertical, mdiPencil, mdiPlus, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { useToast } from '../hooks/use-toast'
import { useApi } from '../api/hooks'
import { MultilingualInputGroup } from './ui/multilingual-input-group'
import {
  usePropertyEnumValues,
  useCreatePropertyEnumValue,
  useUpdatePropertyEnumValue,
  type PropertyEnumValue,
  type CreatePropertyEnumValueRequest
} from '../hooks/useQueries'
import { EmptyState } from './ui/empty-state'

interface SelectValuesManagerProps {
  org: string
  propertyUid: string
}

interface EditingValue {
  uid?: string
  value: string
  name: { [key: string]: string }
  description?: { [key: string]: string }
  order_index: number
}

export function SelectValuesManager({ org, propertyUid }: SelectValuesManagerProps) {
  const { toast } = useToast()
  const { request } = useApi()
  const queryClient = useQueryClient()
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editingValue, setEditingValue] = useState<EditingValue | null>(null)
  const [newValue, setNewValue] = useState<Omit<EditingValue, 'uid'>>({
    value: '',
    name: { en: '', fr: '' },
    description: { en: '', fr: '' },
    order_index: 0
  })

  const { data: enumValues = [], isLoading, error } = usePropertyEnumValues(org, propertyUid)
  const createEnumValue = useCreatePropertyEnumValue(org, propertyUid)
  const updateEnumValue = useUpdatePropertyEnumValue(org, propertyUid)

  // Delete mutation for enum values
  const deleteEnumValueMutation = useMutation({
    mutationFn: async (enumValueUid: string) => {
      await request('/api/v1/organizations/{orgCode}/properties/{propertyUid}/enum-values/{enumValueUid}', 'DELETE', {
        params: {
          path: {
            orgCode: org,
            propertyUid: propertyUid,
            enumValueUid: enumValueUid
          }
        }
      })
      return enumValueUid
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as any[]
          return queryKey.length >= 2 && queryKey[0] === 'get' &&
            queryKey[1] === '/api/v1/organizations/{orgCode}/properties/{propertyUid}/enum-values'
        }
      })
    },
    onError: (error) => {
      console.error('Error deleting enum value:', error)
      toast({
        title: "Error",
        description: "Failed to delete enum value. Please try again.",
        variant: "destructive",
      })
    },
  })

  // Helper function to get display name from multilingual object
  const getDisplayName = (nameObj: { [key: string]: string }) => {
    return nameObj.en || nameObj.fr || Object.values(nameObj)[0] || 'Unnamed'
  }

  // Sort enum values by order_index
  const sortedValues = [...enumValues].sort((a, b) => a.order_index - b.order_index)

  const handleAddValue = useCallback(async () => {
    // Check if value is provided
    if (!newValue.value.trim()) {
      toast({
        title: "Validation Error",
        description: "Value is required",
        variant: "destructive",
      })
      return
    }

    // Check if at least one name is provided
    const hasName = Object.values(newValue.name).some(name => name.trim().length > 0)
    if (!hasName) {
      toast({
        title: "Validation Error",
        description: "At least one name (English or French) is required",
        variant: "destructive",
      })
      return
    }

    // Check for duplicate values
    if (enumValues.some(v => v.value === newValue.value.trim())) {
      toast({
        title: "Validation Error",
        description: "A value with this name already exists",
        variant: "destructive",
      })
      return
    }

    try {
      const createData: CreatePropertyEnumValueRequest = {
        value: newValue.value.trim(),
        name: newValue.name,
        description: Object.values(newValue.description || {}).some(desc => desc.trim()) ? newValue.description : undefined,
        order_index: Math.max(0, ...enumValues.map(v => v.order_index), newValue.order_index) + 1
      }

      await createEnumValue.mutateAsync(createData)

      setIsAddDialogOpen(false)
      setNewValue({ value: '', name: { en: '', fr: '' }, description: { en: '', fr: '' }, order_index: 0 })
    } catch (error) {
      console.error('Error adding enum value:', error)
      toast({
        title: "Error",
        description: "Failed to add enum value. Please try again.",
        variant: "destructive",
      })
    }
  }, [newValue, enumValues, createEnumValue, toast])

  const handleEditValue = useCallback(async () => {
    if (!editingValue || !editingValue.value.trim()) {
      toast({
        title: "Validation Error",
        description: "Value is required",
        variant: "destructive",
      })
      return
    }

    // Check if at least one name is provided
    const hasName = Object.values(editingValue.name).some(name => name.trim().length > 0)
    if (!hasName) {
      toast({
        title: "Validation Error",
        description: "At least one name (English or French) is required",
        variant: "destructive",
      })
      return
    }

    // Check for duplicate values (excluding current one)
    if (enumValues.some(v => v.uid !== editingValue.uid && v.value === editingValue.value.trim())) {
      toast({
        title: "Validation Error",
        description: "A value with this name already exists",
        variant: "destructive",
      })
      return
    }

    try {
      if (!editingValue.uid) {
        toast({
          title: "Error",
          description: "Invalid enum value ID",
          variant: "destructive",
        })
        return
      }
      
      await updateEnumValue.mutateAsync(editingValue.uid, {
        value: editingValue.value.trim(),
        name: editingValue.name,
        description: Object.values(editingValue.description || {}).some(desc => desc.trim()) ? editingValue.description : undefined,
        order_index: editingValue.order_index
      })

      setEditingValue(null)
      setIsEditDialogOpen(false)
    } catch (error) {
      console.error('Error updating enum value:', error)
      toast({
        title: "Error",
        description: "Failed to update enum value. Please try again.",
        variant: "destructive",
      })
    }
  }, [editingValue, enumValues, updateEnumValue, toast])

  // The colour is stored on the value (`properties.color`) and is what every
  // thematic plan paints with, so it saves on change rather than with the form.
  const handleColourChange = useCallback(async (enumValue: PropertyEnumValue, colour: string) => {
    try {
      await updateEnumValue.mutateAsync(enumValue.uid, { properties: { color: colour } })
      queryClient.invalidateQueries({ queryKey: ['thematics'] })
    } catch (error) {
      console.error('Error updating enum value colour:', error)
      toast({
        title: "Error",
        description: "Failed to update the colour. Please try again.",
        variant: "destructive",
      })
    }
  }, [updateEnumValue, queryClient, toast])

  const handleDeleteValue = useCallback(async (enumValue: PropertyEnumValue) => {
    if (!confirm(`Are you sure you want to delete the value "${getDisplayName(enumValue.name)}"? This action cannot be undone.`)) {
      return
    }

    deleteEnumValueMutation.mutate(enumValue.uid)
  }, [deleteEnumValueMutation])

  const startEdit = useCallback((enumValue: PropertyEnumValue) => {
    setEditingValue({
      uid: enumValue.uid,
      value: enumValue.value,
      name: enumValue.name,
      description: enumValue.description,
      order_index: enumValue.order_index
    })
    setIsEditDialogOpen(true)
  }, [])

  const cancelEdit = useCallback(() => {
    setEditingValue(null)
    setIsEditDialogOpen(false)
  }, [])

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Enum Values</h3>
        </div>
        <div className="flex items-center justify-center p-8">
          <div className="motion-safe:animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
          <span className="text-muted-foreground">Loading enum values...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Enum Values</h3>
        </div>
        <div className="flex flex-col items-center justify-center p-8">
          <p className="text-destructive mb-2">Error loading enum values</p>
          <p className="text-sm text-muted-foreground">
            {(() => {
              if (typeof error === 'object' && error !== null) {
                if ('title' in error && typeof (error as any).title === 'string') {
                  return (error as any).title
                }
                if ('message' in error && typeof (error as any).message === 'string') {
                  return (error as any).message
                }
              }
              return 'Unknown error occurred'
            })()}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Enum Values</h3>
          <p className="text-sm text-muted-foreground">
            Define the available options for this enum property
          </p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="add-enum-value">
              <AppIcon path={mdiPlus} size="sm" className="mr-2" />
              Add Enum Value
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Enum Value</DialogTitle>
              <DialogDescription>
                Create a new option for this enum property
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-value">Value *</Label>
                <Input
                  id="new-value"
                  value={newValue.value}
                  onChange={(e) => setNewValue(prev => ({ ...prev, value: e.target.value }))}
                  placeholder="e.g., residential"
                  data-testid="new-value-input"
                />
              </div>

              <MultilingualInputGroup
                nameValue={newValue.name}
                descriptionValue={newValue.description || { en: '', fr: '' }}
                onNameChange={(name) => setNewValue(prev => ({ ...prev, name }))}
                onDescriptionChange={(description) => setNewValue(prev => ({ ...prev, description }))}
                namePlaceholder="e.g., Residential"
                descriptionPlaceholder="e.g., Type of residential property"
                nameRequired
                nameLabel="Name"
                descriptionLabel="Description (optional)"
                data-testid-name="new-name-input"
                data-testid-description="new-description-input"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsAddDialogOpen(false)}
                data-testid="cancel-add"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddValue}
                disabled={createEnumValue.isPending}
                data-testid="confirm-add"
              >
                {createEnumValue.isPending ? 'Adding...' : 'Add Enum Value'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {sortedValues.length === 0 ? (
        <EmptyState
          title="No enum values defined"
          description="Add values to make this enum property functional"
        />
      ) : (
        <div className="border border-border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>Value</TableHead>
                <TableHead className="w-20">Colour</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Order</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedValues.map((enumValue) => (
                <TableRow key={enumValue.uid}>
                  <TableCell>
                    <AppIcon path={mdiDragVertical} size="sm" tone="muted" className="cursor-move" />
                  </TableCell>
                  <TableCell>
                    <code className="text-sm bg-muted px-2 py-1 rounded">
                      {enumValue.value}
                    </code>
                  </TableCell>
                  <TableCell>
                    <input
                      type="color"
                      aria-label={`Colour of ${getDisplayName(enumValue.name)}`}
                      value={(enumValue.properties as { color?: string } | undefined)?.color || '#94a3b8'}
                      onChange={(e) => handleColourChange(enumValue, e.target.value)}
                      className="h-7 w-10 cursor-pointer rounded border border-border bg-transparent p-0.5"
                      data-testid={`enum-colour-${enumValue.uid}`}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-medium">{getDisplayName(enumValue.name)}</div>
                      <div className="text-sm text-muted-foreground">
                        {enumValue.name.en && <span>🇺🇸 {enumValue.name.en}</span>}
                        {enumValue.name.en && enumValue.name.fr && ' • '}
                        {enumValue.name.fr && <span>🇫🇷 {enumValue.name.fr}</span>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {enumValue.description && (
                      <div className="text-sm text-muted-foreground max-w-xs">
                        {enumValue.description.en && <div>🇺🇸 {enumValue.description.en}</div>}
                        {enumValue.description.fr && <div>🇫🇷 {enumValue.description.fr}</div>}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {enumValue.order_index}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEdit(enumValue)}
                        data-testid={`edit-${enumValue.uid}`}
                      >
                        <AppIcon path={mdiPencil} size="sm" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteValue(enumValue)}
                        disabled={deleteEnumValueMutation.isPending}
                        className="text-destructive hover:text-destructive disabled:opacity-50"
                        data-testid={`delete-${enumValue.uid}`}
                      >
                        <AppIcon path={mdiTrashCanOutline} size="sm" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Enum Value</DialogTitle>
            <DialogDescription>
              Update the enum value details
            </DialogDescription>
          </DialogHeader>
          {editingValue && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-value">Value *</Label>
                <Input
                  id="edit-value"
                  value={editingValue.value}
                  onChange={(e) => setEditingValue(prev => prev ? { ...prev, value: e.target.value } : null)}
                  placeholder="e.g., residential"
                  data-testid="edit-value-input"
                />
              </div>

              <MultilingualInputGroup
                nameValue={editingValue.name}
                descriptionValue={editingValue.description || { en: '', fr: '' }}
                onNameChange={(name) => setEditingValue(prev => prev ? { ...prev, name } : null)}
                onDescriptionChange={(description) => setEditingValue(prev => prev ? { ...prev, description } : null)}
                namePlaceholder="e.g., Residential"
                descriptionPlaceholder="e.g., Type of residential property"
                nameRequired
                nameLabel="Name"
                descriptionLabel="Description (optional)"
                data-testid-name="edit-name-input"
                data-testid-description="edit-description-input"
              />
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={cancelEdit}
              data-testid="cancel-edit"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditValue}
              disabled={updateEnumValue.isPending}
              data-testid="confirm-edit"
            >
              {updateEnumValue.isPending ? 'Updating...' : 'Update Enum Value'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}